/*
  Where people's legal entity is today, and where it becomes.

  THE FIRST SVG IN THIS CLIENT. Every other chart here is flexbox divs with percentage heights, which
  works because they are all bars — a bar is a rectangle and CSS draws rectangles. This one is a
  bipartite flow: the ribbons are cubic curves between two columns whose vertical positions are
  independent, and there is no honest way to express that as a box model. Ported from
  `renderEntitySankey` (orgchart.html:8427-8470), including its geometry.

  IT SHIPS A TABLE TWIN like every other chart in this codebase, in a `<details>`. That is not
  decoration here: the diagram's whole content is proportion, and proportion is exactly what a screen
  reader, a printout and a test cannot take from it.

  COLOUR IS NEVER THE ONLY CHANNEL. A ribbon's colour is its TARGET's classification, and the same
  classification is written in the table twin and in the legend. Retained-in-place is drawn paler
  rather than in its own hue, because it is the absence of a move rather than a fifth kind of one.
*/
import { ENTITY_CLASS_META, cssVarOf, type EntityFlows } from '@integrate/core';
import type { EntityClassification } from '@integrate/contracts';

const W = 760;
const NODE_W = 13;
const GAP = 9;

export function EntitySankey({
  flows,
  classOf,
  format,
  focus,
  onFocus,
}: {
  flows: EntityFlows;
  classOf(name: string): EntityClassification;
  format(value: number): string;
  /** A target the reader has clicked, dimming everything that does not reach it. */
  focus: string | null;
  onFocus(name: string | null): void;
}): JSX.Element {
  const { sources, targets, srcTotals, tgtTotals, links } = flows;

  if (!links.length) {
    return (
      <p className="muted" data-testid="entity-sankey-empty">
        No legal-entity data to chart. Map a Legal Entity column to use this view.
      </p>
    );
  }

  const rows = Math.max(sources.length, targets.length);
  const H = Math.min(640, Math.max(220, rows * 30 + 24));

  /*
    Two scales, not one.

    The columns carry the same total but a different number of nodes, so a shared scale would leave
    the sparser side short and make the diagram look as though people had gone missing. Each side is
    scaled to fill the height, which is what makes the ribbons meet.
  */
  const scaleFor = (names: string[], totals: Record<string, number>): number => {
    const sum = names.reduce((n, k) => n + (totals[k] ?? 0), 0);
    const available = H - GAP * Math.max(0, names.length - 1);
    return sum > 0 ? available / sum : 0;
  };
  const srcScale = scaleFor(sources, srcTotals);
  const tgtScale = scaleFor(targets, tgtTotals);

  const place = (
    names: string[],
    totals: Record<string, number>,
    scale: number,
  ): Record<string, { y: number; h: number }> => {
    const out: Record<string, { y: number; h: number }> = {};
    let y = 0;
    for (const name of names) {
      const h = Math.max(1, (totals[name] ?? 0) * scale);
      out[name] = { y, h };
      y += h + GAP;
    }
    return out;
  };
  const srcPos = place(sources, srcTotals, srcScale);
  const tgtPos = place(targets, tgtTotals, tgtScale);

  // Running offsets: each ribbon stacks inside its node rather than all leaving from the middle.
  const srcUsed: Record<string, number> = {};
  const tgtUsed: Record<string, number> = {};

  const ribbons = links.map((link) => {
    const s = srcPos[link.source]!;
    const t = tgtPos[link.target]!;
    const sh = Math.max(1, link.value * srcScale);
    const th = Math.max(1, link.value * tgtScale);
    const sy = s.y + (srcUsed[link.source] ?? 0);
    const ty = t.y + (tgtUsed[link.target] ?? 0);
    srcUsed[link.source] = (srcUsed[link.source] ?? 0) + sh;
    tgtUsed[link.target] = (tgtUsed[link.target] ?? 0) + th;

    const x1 = NODE_W;
    const x2 = W - NODE_W;
    const mid = (x1 + x2) / 2;
    const d = [
      `M ${x1} ${sy}`,
      `C ${mid} ${sy}, ${mid} ${ty}, ${x2} ${ty}`,
      `L ${x2} ${ty + th}`,
      `C ${mid} ${ty + th}, ${mid} ${sy + sh}, ${x1} ${sy + sh}`,
      'Z',
    ].join(' ');

    const inPlace = link.source === link.target;
    const dimmed = focus !== null && link.target !== focus;
    return { link, d, inPlace, dimmed };
  });

  return (
    <figure className="ent-sankey" data-testid="entity-sankey">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="img"
        aria-label={`Legal-entity flows: ${sources.length} source entities to ${targets.length} target entities`}
      >
        {ribbons.map(({ link, d, inPlace, dimmed }, i) => (
          <path
            key={`${link.source}→${link.target}-${i}`}
            d={d}
            data-source={link.source}
            data-target={link.target}
            data-value={link.value}
            fill={inPlace ? cssVarOf('muted') : cssVarOf(ENTITY_CLASS_META[classOf(link.target)].color)}
            opacity={dimmed ? 0.06 : inPlace ? 0.16 : 0.34}
          >
            <title>{`${link.source} → ${link.target}: ${format(link.value)}`}</title>
          </path>
        ))}

        {sources.map((name) => {
          const p = srcPos[name]!;
          return (
            <g key={`s-${name}`}>
              <rect x={0} y={p.y} width={NODE_W} height={p.h} fill={cssVarOf('muted')} />
              <text x={NODE_W + 6} y={p.y + p.h / 2} dominantBaseline="middle" className="ent-node-label">
                {name} · {format(srcTotals[name] ?? 0)}
              </text>
            </g>
          );
        })}

        {targets.map((name) => {
          const p = tgtPos[name]!;
          const on = focus === name;
          return (
            <g
              key={`t-${name}`}
              data-testid={`entity-target-${name}`}
              style={{ cursor: 'pointer' }}
              onClick={() => onFocus(on ? null : name)}
            >
              <rect
                x={W - NODE_W}
                y={p.y}
                width={NODE_W}
                height={p.h}
                fill={cssVarOf(ENTITY_CLASS_META[classOf(name)].color)}
                opacity={on ? 1 : 0.75}
              />
              <text
                x={W - NODE_W - 6}
                y={p.y + p.h / 2}
                dominantBaseline="middle"
                textAnchor="end"
                className="ent-node-label"
              >
                {name} · {format(tgtTotals[name] ?? 0)}
              </text>
            </g>
          );
        })}
      </svg>

      <figcaption className="muted small">
        Ribbon width is the metric above. Colour is the target entity&rsquo;s role; grey is people
        whose entity does not change.
      </figcaption>

      <div className="ent-legend" aria-hidden="true">
        {(Object.keys(ENTITY_CLASS_META) as EntityClassification[]).map((k) => (
          <span key={k} className="ent-legend-item">
            <i style={{ background: cssVarOf(ENTITY_CLASS_META[k].color) }} />
            {ENTITY_CLASS_META[k].label}
          </span>
        ))}
        <span className="ent-legend-item">
          <i style={{ background: cssVarOf('muted') }} />
          Retained in place
        </span>
      </div>

      {/* The chart's content as a table, for everyone the diagram does not serve. */}
      <details className="chart-table">
        <summary>Show these flows as a table</summary>
        <table className="tsa-table" data-testid="entity-sankey-table">
          <thead>
            <tr>
              <th scope="col">From (as-is)</th>
              <th scope="col">To</th>
              <th scope="col">Role</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>
            {links.map((link, i) => (
              <tr key={`${link.source}→${link.target}-${i}`}>
                <td>{link.source}</td>
                <td>{link.target}</td>
                <td>{ENTITY_CLASS_META[classOf(link.target)].label}</td>
                <td className="num">{format(link.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
